import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CheckCheckIcon, PuzzleIcon, RefreshIcon} from './icons';
import createAxiosInstance from '../support/AxiosSupport';
import AuthImage from './AuthImage';
import {slotForCategoryCode} from './outfit-builder/steps';

/** Одно ребро графа связей с точки зрения открытой вещи (контракт `GET /api/item-relations/{id}`). */
export interface ItemRelation {
    targetItemId: string;
    targetItemName: string | null;
    targetBrandName: string | null;
    targetCategoryName: string | null;
    targetCategoryId: string | null;
    targetCategoryCode: string | null;
    targetPhotoUrl: string | null;
    targetAvailability: string | null;
    targetAvailabilityLabel: string | null;
    relationType: string;
    relationLabel: string | null;
    score: number | null;
}

interface RelationsResult {
    sourceItemId: string;
    relations: ItemRelation[];
    graphReady: boolean;
}

interface Props {
    itemId: string;
    ownerId: string | null;
    /** Слот конструктора для открытой вещи; без него якорь в состав образа не положить. */
    anchorSlot: string | null;
    /** Название и фото якоря — конструктор принимает состав целиком, а не по id. */
    anchorName: string;
    anchorBrandName?: string | null;
    anchorPhotoUrl?: string | null;
    anchorCategoryId?: string | null;
    /** Вещь в корзине или выбыла: показываем связи, но собирать из неё образ не предлагаем. */
    frozen?: boolean;
}

/** Сколько связей показываем: две строки сетки на десктопе. */
const LIMIT = 6;

const ITEM_ROUTE = '/clothing/';

/**
 * Блок «Лучшие сочетания» карточки вещи (CTHS-135, AC-3): вещи из **персистированного графа**
 * связей (`ItemRelation`, типы `COMPATIBLE`/`ACCEPTABLE`), отсортированные по силе связи.
 *
 * <p>Это не дубль блока «С чем носить» на вкладке «Образы»: тот считает пары на лету под ручную
 * сборку образа, а здесь — накопленная ночным пересчётом картина гардероба и вход в конструктор
 * с несколькими вещами сразу. Непригодные и выбывшие партнёры отфильтрованы на сервере
 * (`ItemAvailability`), поэтому клиент не пересказывает это правило, а только показывает
 * оговорку по статусу.</p>
 */
const ItemBestMatches: React.FC<Props> = ({
    itemId, ownerId, anchorSlot, anchorName, anchorBrandName = null, anchorPhotoUrl = null,
    anchorCategoryId = null, frozen = false,
}) => {
    const navigate = useNavigate();
    const [result, setResult] = useState<RelationsResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<RelationsResult>(`/item-relations/${itemId}`, {
                params: {
                    ...(ownerId ? {ownerId} : {}),
                    relationType: ['COMPATIBLE', 'ACCEPTABLE'],
                    limit: LIMIT,
                },
                // Повторяемый параметр `relationType=COMPATIBLE&relationType=ACCEPTABLE`:
                // Spring связывает его со списком типов (CTHS-135, FR-1).
                paramsSerializer: {indexes: null},
            });
            setResult(res.data);
        } catch (e) {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [itemId, ownerId]);

    useEffect(() => {
        setSelected([]);
        void load();
    }, [load]);

    const toggle = (id: string) =>
        setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const relations = result?.relations ?? [];

    /**
     * Конструктор принимает состав через `state.prefillItems` со слотами — тот же контракт, что у
     * кнопки «Собрать образ» карточки вещи. Вещь, чья категория не ложится ни в один слот, в
     * состав не попадает: класть её некуда, а молча терять якорь нельзя.
     */
    const buildOutfit = () => {
        const picked = relations
            .filter(r => selected.includes(r.targetItemId))
            .map(r => ({
                id: r.targetItemId,
                name: r.targetItemName ?? 'Вещь',
                brandName: r.targetBrandName,
                primaryPhotoUrl: r.targetPhotoUrl,
                slot: slotForCategoryCode(r.targetCategoryCode),
                categoryId: r.targetCategoryId,
            }))
            .filter(it => it.slot !== null);
        const anchor = anchorSlot
            ? [{
                id: itemId,
                name: anchorName,
                brandName: anchorBrandName,
                primaryPhotoUrl: anchorPhotoUrl,
                slot: anchorSlot,
                categoryId: anchorCategoryId,
            }]
            : [];
        navigate('/outfits/builder', {state: {prefillItems: [...anchor, ...picked]}});
    };

    const header = (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Лучшие сочетания</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Из графа связей гардероба: пары, которые система нашла при ночном пересчёте.
            </p>
        </div>
    );

    if (loading) {
        return (
            <section className="card p-5 space-y-4" data-testid="item-best-matches-loading">
                {header}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" aria-hidden="true">
                    {Array.from({length: 4}).map((_, i) => (
                        <div key={i} className="animate-pulse">
                            <div className="aspect-square rounded-xl bg-gray-100 dark:bg-ink-700"/>
                            <div className="h-3 w-3/4 mt-2 rounded bg-gray-100 dark:bg-ink-700"/>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (failed) {
        return (
            <section className="card p-5 space-y-3" data-testid="item-best-matches-error">
                {header}
                <p className="text-sm text-gray-600 dark:text-gray-300">Не удалось загрузить сочетания</p>
                <button type="button" onClick={() => void load()} className="btn-secondary min-h-[44px]"
                        data-testid="item-best-matches-retry">
                    <RefreshIcon size={16}/>
                    Повторить
                </button>
            </section>
        );
    }

    if (relations.length === 0) {
        const graphReady = result?.graphReady ?? false;
        return (
            <section className="card p-5 space-y-3" data-testid="item-best-matches-empty">
                {header}
                <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                    <PuzzleIcon className="shrink-0 text-gray-400 dark:text-gray-500 mt-0.5" size={20}/>
                    <p data-testid="item-best-matches-empty-reason">
                        {graphReady
                            ? 'Подходящих сочетаний для этой вещи в графе пока нет — добавьте вещи или уточните их характеристики.'
                            : 'Граф связей ещё не построен. Он пересчитывается ночью для всего гардероба, после этого сочетания появятся здесь.'}
                    </p>
                </div>
                <button type="button" onClick={() => navigate(`${ITEM_ROUTE}${itemId}/outfits`)}
                        className="btn-secondary min-h-[44px]" data-testid="item-best-matches-fallback">
                    Посмотреть подбор на лету
                </button>
            </section>
        );
    }

    return (
        <section className="card p-5 space-y-4" data-testid="item-best-matches">
            {header}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {relations.map(r => {
                    const picked = selected.includes(r.targetItemId);
                    const name = r.targetItemName ?? 'Вещь';
                    return (
                        <div key={r.targetItemId}
                             data-testid={`item-best-matches-card-${r.targetItemId}`}
                             className={`rounded-xl border overflow-hidden transition-colors ${
                                 picked
                                     ? 'border-brand-500 ring-1 ring-brand-500/40'
                                     : 'border-gray-200 dark:border-white/10 hover:border-brand-400'
                             }`}>
                            <button type="button" onClick={() => navigate(`${ITEM_ROUTE}${r.targetItemId}`)}
                                    aria-label={`Открыть карточку вещи «${name}»`}
                                    data-testid={`item-best-matches-open-${r.targetItemId}`}
                                    className="block w-full text-left">
                                <span className="block aspect-square bg-gray-100 dark:bg-ink-700">
                                    {r.targetPhotoUrl && (
                                        <AuthImage imageKey={r.targetPhotoUrl} alt={name} variant="thumbnail"
                                                   className="w-full h-full object-cover"/>
                                    )}
                                </span>
                                <span className="block p-2">
                                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {name}
                                    </span>
                                    {r.targetCategoryName && (
                                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {r.targetCategoryName}
                                        </span>
                                    )}
                                    <span className="flex flex-wrap gap-1 mt-1.5">
                                        {r.relationLabel && <span className="chip">{r.relationLabel}</span>}
                                        {r.score !== null && r.score !== undefined && (
                                            <span className="chip" data-testid={`item-best-matches-score-${r.targetItemId}`}>
                                                {r.score}/100
                                            </span>
                                        )}
                                        {r.targetAvailabilityLabel && (
                                            <span className="chip text-amber-700 dark:text-amber-300">
                                                {r.targetAvailabilityLabel}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </button>
                            {!frozen && (
                                <div className="px-2 pb-2">
                                    <button type="button" onClick={() => toggle(r.targetItemId)}
                                            aria-pressed={picked}
                                            data-testid={`item-best-matches-select-${r.targetItemId}`}
                                            className={`min-h-[44px] w-full inline-flex items-center justify-center gap-1.5
                                                        rounded-xl text-xs font-medium transition-colors ${
                                                picked
                                                    ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300'
                                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                                            }`}>
                                        {picked && <CheckCheckIcon size={14}/>}
                                        {picked ? 'Выбрана' : 'Выбрать'}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {!frozen && (
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={buildOutfit} disabled={selected.length === 0}
                            className="btn-primary min-h-[44px]" data-testid="item-best-matches-build-btn">
                        Собрать образ с этими
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {selected.length === 0
                            ? 'Отметьте вещи, с которыми собрать образ'
                            : `Выбрано: ${selected.length}`}
                    </span>
                </div>
            )}
        </section>
    );
};

export default ItemBestMatches;
